import React from 'react';
import { Icon } from '@repo/ui';
import { Button } from '@repo/ui';
import type { ConfirmDialogState } from '../types';

interface ConfirmActionDialogProps {
  dialog: ConfirmDialogState;
  onConfirm: () => void;
  onCancel: () => void;
  isProcessing: boolean;
}

export const ConfirmActionDialog: React.FC<ConfirmActionDialogProps> = ({
  dialog,
  onConfirm,
  onCancel,
  isProcessing,
}) => {
  if (!dialog.show) return null;

  const getTitle = () => {
    switch (dialog.action) {
      case 'delete':
        return 'Confirm Deletion';
      case 'delete_clio_only':
        return 'Confirm Clio Deletion';
      case 'delete_clio':
        return 'Confirm Clio Deletion';
      default:
        return 'Confirm Recreation';
    }
  };

  const getMessage = () => {
    switch (dialog.action) {
      case 'delete':
        return `This will delete the Clio webhook for ${dialog.modelName} and remove all associated SQS queues and Lambda triggers. This action cannot be undone.`;
      case 'delete_clio_only':
      case 'delete_clio':
        return `This will only delete the orphaned ${dialog.modelName} webhook from Clio. It will not affect any local configuration since this webhook doesn't exist in our system.`;
      default:
        return `This will recreate the Clio webhook for ${dialog.modelName} with auto-processing enabled. The existing webhook will be deleted and recreated.`;
    }
  };

  const getButtonText = () => {
    switch (dialog.action) {
      case 'delete':
        return 'Delete Everything';
      case 'delete_clio':
      case 'delete_clio_only':
        return 'Delete from Clio Only';
      default:
        return 'Recreate Webhook';
    }
  };

  const isDangerousAction =
    dialog.action === 'delete' || dialog.action === 'delete_clio' || dialog.action === 'delete_clio_only';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
        <div className="flex items-center mb-4">
          <Icon name="HiExclamation" className="h-6 w-6 text-yellow-600 mr-2" />
          <h3 className="text-lg font-medium text-xtnd-dark dark:text-xtnd-white">{getTitle()}</h3>
        </div>

        <p className="text-gray-600 dark:text-gray-400 mb-6">{getMessage()}</p>

        <div className="flex justify-end space-x-3">
          <Button onClick={onCancel} variant="outline" disabled={isProcessing}>
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            loading={isProcessing}
            variant={isDangerousAction ? 'outline' : 'primary'}
            className={
              isDangerousAction
                ? 'text-red-600 hover:text-red-700 border-red-300 hover:border-red-400'
                : ''
            }
          >
            {getButtonText()}
          </Button>
        </div>
      </div>
    </div>
  );
};

